import Heading from "../../../components/Heading";
import Text from "../../../components/Text";
import React, {useEffect} from "react";
import {useAdmin} from "../../../contexts/AdminContext";
import IconButton from "../../../components/IconButton";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import Card from "../../../components/Card";
import Button from "../../../components/Button";
import {useGlobalContext} from "../../../contexts/globalContext";
import {useTranslation} from "react-i18next";
import cx from "classnames";
import styles from "./ChangePlan.module.css";
import ConfirmModal from "../../../components/ConfirmModal";

const ChangePlan: React.FC<{changePage: (p: string) => void}> = ({changePage}) => {
    const { showToast, showModal, closeModal } = useGlobalContext()
    const { plans, activeSub, getPlans, service, getSubscriptions, getSubscription } = useAdmin()
    const {t} = useTranslation(['settings'])
    const { plugin } = useGlobalContext()

    useEffect(() => {
        getPlans()
    }, [getPlans])

    const addPlan = (plan_id: string) => {
        if (plugin) service.addPlanToSub(plugin.plugin, plan_id).then((res) => {
            showToast('top-right', 'success', t('subscription.planSuccessfullyAdded'))
            getSubscription()
            getSubscriptions()
            changePage('overview')
        }).catch(() => {
            showToast('top-right', 'error', t('subscription.errorBookingThePlan'))
        })
    }

    const removePlan = (plan_id: string) => {
        if (plugin) service.removePlanFromSub(plugin.plugin, plan_id).then((res) => {
            showToast('top-right', 'success', t('subscription.planSuccessfullyTerminated'))
            getSubscription()
            getSubscriptions()
            closeModal()
            changePage('overview')
        }).catch(() => {
            showToast('top-right', 'error', t("subscription.errorPlanTerminated"))
        })
    }

    const showRemoveModal = (plan_id: string) => {
        showModal({
            heading: t('common.confirmation'),
            content: <ConfirmModal text={t('subscription.removePlanQuestion')} onSubmit={() => removePlan(plan_id)}/>, fullscreen: false
        });
    }

    const showStopModal = () => {
        showModal({
            heading: t('common.confirmation'),
            content: <ConfirmModal text={t('subscription.stopSubQuestion')} onSubmit={() => stopSub()}/>, fullscreen: false
        });
    }

    const stopSub = () => {
        if (plugin) service.stopSub(plugin.plugin).then((res) => {
            showToast('top-right', 'success', t('subscription.subscriptionSuccessfullyTerminated'))
            getSubscription()
            getSubscriptions()
            closeModal()
            changePage('overview')
        }).catch(() => {
            showToast('top-right', 'error', t('subscription.errorTerminatingTheSubscription'))
        })
    }

    const resumeSub = () => {
        if (plugin) service.resumeSub(plugin.plugin).then((res) => {
            showToast('top-right', 'success', t("subscription.subscriptionSuccessfullyReactivated"))
            getSubscription()
            getSubscriptions()
            changePage('overview')
        }).catch(() => {
            showToast('top-right', 'error', t("subscription.errorReactivatingTheSubscription"))
        })
    }

    const changeHost = () => {
        if (plugin && activeSub) service.changeHost(plugin.plugin, {
            host: window.location.host,
            key: activeSub.stripe_id
        }).then((res) => {
            showToast('top-right', 'success', t("subscription.subscriptionChangedHostSuccessful"))
            getSubscription()
            getSubscriptions()
            changePage('overview')
        }).catch(() => {
            showToast('top-right', 'error', t("subscription.subscriptionChangedHostError"))
        })
    }

    return (
        <div>
            <Heading level={3} classes={'mb-4'}>{t("subscription.makeChangesToTheSubscription")}</Heading>
            <IconButton
                icon={faArrowLeft}
                color={'black'}
                onClick={() => changePage('overview')}
            >
                {t("subscription.back")}
            </IconButton>
            <Card classes={'my-3'}>
                <Heading level={4}>{t("subscription.connectSubscription")}</Heading>
                <Text classes={'my-3'}>{t("subscription.makeChangesToTheSubsText")}
                </Text>
                <Button classes={'mt-3'} hierarchy={'primary'} text={t("subscription.connectSubscription")} fn={() => changeHost()}/>
            </Card>
            <Heading level={3} classes={'mt-6 mb-4'}>{t("subscription.editPlans")}</Heading>
            <div className={cx(styles['plans'])}>
                {plans.filter((p) => !!p.dependent_id).map(p => {
                   return (
                       <Card key={p.id}>
                           <Heading level={4}>{p.name} ({p.price.toString().replace('.', ',')} €)</Heading>
                           <div className={cx(styles['planDescription'])}>
                               <Text>{p.description}</Text>
                           </div>
                           <div className={cx(styles['planButtons'])}>
                               {activeSub?.plans.map((a) => a.id).includes(p.id)
                                   ? <Button hierarchy={'secondary'} text={t("subscription.endPlan")} fn={() => showRemoveModal(p.stripe_plan)}/>
                                   : <Button hierarchy={'primary'} text={t("subscription.addPlan")} fn={() => addPlan(p.stripe_plan)}/>
                               }
                           </div>

                       </Card>
                   )
                })}
            </div>
            {!activeSub?.end_date ?
                <>
                    <Heading level={3} classes={'mt-6 mb-4'}>{t("subscription.cancelSubscription")}</Heading>
                    <Card classes={'mb-4'}>
                        <Text classes={'my-3'}>{t("subscription.cancelSubsText")}</Text>
                        <Button hierarchy={'secondary'} text={t("subscription.cancelSubscription")} fn={() => showStopModal()}/>
                    </Card>
                </> :
                <>
                <Heading level={3} classes={'mt-6 mb-4'}>{t("subscription.reactivateSubscription")}</Heading>
                    <Card classes={'mb-4'}>
                        <Text classes={'my-3'}>
                            {t("subscription.reactivateSubscriptionText")}
                        </Text>
                        <Button hierarchy={'secondary'} text={t("subscription.reactivateSubscription")} fn={() => resumeSub()}/>
                    </Card>
                </>
            }
        </div>
    )
}

export default ChangePlan
